import { supabase } from '@/lib/supabase'
import type { PartyAction, CreatePartyActionInput, ActionType, ActionStatus } from '@/types'

// Query key factory for consistent cache management
export const partyActionKeys = {
  all: ['partyActions'] as const,
  byParty: (partyId: string) => ['partyActions', 'party', partyId] as const,
  byPartyTick: (partyId: string, tick: number) =>
    ['partyActions', 'party', partyId, 'tick', tick] as const,
  queued: (partyId: string, tick: number) =>
    ['partyActions', 'queued', partyId, tick] as const,
  queuedForTick: (tick: number, nationId: string) =>
    ['partyActions', 'queuedForTick', tick, nationId] as const,
  detail: (id: string) => ['partyActions', 'id', id] as const,
  history: (partyId: string, filters: ActionHistoryFilters) =>
    ['partyActions', 'history', partyId, filters] as const,
}

export interface ActionHistoryFilters {
  actionType?: ActionType
  status?: ActionStatus
  tickFrom?: number
  tickTo?: number
  page: number
  pageSize: number
}

export interface ActionHistoryResult {
  actions: PartyAction[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export interface ActionTargetNames {
  [targetId: string]: string
}

/**
 * Fetch paginated action history for a party with filtering.
 * Returns actions ordered by tick desc, then created_at desc.
 */
export async function fetchActionHistory(
  partyId: string,
  filters: ActionHistoryFilters
): Promise<ActionHistoryResult> {
  const { actionType, status, tickFrom, tickTo, page, pageSize } = filters
  const from = page * pageSize
  const to = from + pageSize - 1

  let query = supabase
    .from('party_actions')
    .select('*', { count: 'exact' })
    .eq('party_id', partyId)
    .order('tick', { ascending: false })
    .order('created_at', { ascending: false })
    .range(from, to)

  if (actionType) {
    query = query.eq('action_type', actionType)
  }
  if (status) {
    query = query.eq('status', status)
  }
  if (tickFrom !== undefined) {
    query = query.gte('tick', tickFrom)
  }
  if (tickTo !== undefined) {
    query = query.lte('tick', tickTo)
  }

  const { data, error, count } = await query

  if (error) {
    throw error
  }

  const totalCount = count ?? 0

  return {
    actions: data as PartyAction[],
    totalCount,
    page,
    pageSize,
    totalPages: Math.ceil(totalCount / pageSize),
  }
}

/**
 * Resolve target IDs to human-readable names.
 * Batches lookups by target_type for efficiency.
 */
export async function resolveTargetNames(
  actions: PartyAction[]
): Promise<ActionTargetNames> {
  const names: ActionTargetNames = {}

  // Group target_ids by type
  const districtIds = new Set<string>()
  const partyIds = new Set<string>()
  const candidateIds = new Set<string>()

  for (const action of actions) {
    if (!action.target_id) continue
    switch (action.target_type) {
      case 'district':
        districtIds.add(action.target_id)
        break
      case 'party':
        partyIds.add(action.target_id)
        break
      case 'candidate':
        candidateIds.add(action.target_id)
        break
    }
  }

  // Fetch in parallel
  const promises: PromiseLike<void>[] = []

  if (districtIds.size > 0) {
    promises.push(
      supabase
        .from('districts')
        .select('id, name')
        .in('id', [...districtIds])
        .then(({ data }) => {
          for (const d of data ?? []) {
            names[d.id] = d.name
          }
        })
    )
  }

  if (partyIds.size > 0) {
    promises.push(
      supabase
        .from('parties')
        .select('id, name')
        .in('id', [...partyIds])
        .then(({ data }) => {
          for (const p of data ?? []) {
            names[p.id] = p.name
          }
        })
    )
  }

  if (candidateIds.size > 0) {
    promises.push(
      supabase
        .from('candidates')
        .select('id, name')
        .in('id', [...candidateIds])
        .then(({ data }) => {
          for (const c of data ?? []) {
            names[c.id] = c.name
          }
        })
    )
  }

  await Promise.all(promises)

  return names
}

/**
 * Fetch all actions for a party, optionally filtered by tick
 */
export async function fetchActionsByParty(
  partyId: string,
  tick?: number
): Promise<PartyAction[]> {
  let query = supabase
    .from('party_actions')
    .select('*')
    .eq('party_id', partyId)
    .order('created_at', { ascending: false })

  if (tick !== undefined) {
    query = query.eq('tick', tick)
  }

  const { data, error } = await query

  if (error) {
    throw error
  }

  return data as PartyAction[]
}

/**
 * Fetch queued actions for a party for a specific tick
 * Used to display pending actions in UI
 */
export async function fetchQueuedActions(
  partyId: string,
  tick: number
): Promise<PartyAction[]> {
  const { data, error } = await supabase
    .from('party_actions')
    .select('*')
    .eq('party_id', partyId)
    .eq('tick', tick)
    .eq('status', 'queued')
    .order('created_at', { ascending: false })

  if (error) {
    throw error
  }

  return data as PartyAction[]
}

/**
 * Fetch all queued actions across all parties in a nation for a specific tick
 * Used by tick processor to process all actions
 */
export async function fetchQueuedActionsForTick(
  tick: number,
  nationId: string
): Promise<PartyAction[]> {
  const { data, error } = await supabase
    .from('party_actions')
    .select(`
      *,
      parties!inner (
        nation_id
      )
    `)
    .eq('tick', tick)
    .eq('status', 'queued')
    .eq('parties.nation_id', nationId)
    .order('created_at', { ascending: true })

  if (error) {
    throw error
  }

  // Strip the joined parties data, return just the action
  return (data as (PartyAction & { parties: { nation_id: string } })[]).map(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ parties, ...action }) => action as PartyAction
  )
}

/**
 * Create a new party action with 'queued' status
 */
export async function createAction(
  action: CreatePartyActionInput
): Promise<PartyAction> {
  const { data, error } = await supabase
    .from('party_actions')
    .insert({
      party_id: action.party_id,
      tick: action.tick,
      action_type: action.action_type,
      action_subtype: action.action_subtype ?? null,
      target_type: action.target_type,
      target_id: action.target_id ?? null,
      action_points_spent: action.action_points_spent,
      parameters: action.parameters ?? {},
      status: 'queued',
    })
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as PartyAction
}

/**
 * Cancel a queued action
 * Only works if current status is 'queued'
 */
export async function cancelAction(actionId: string): Promise<PartyAction> {
  // First verify the action is queued
  const { data: existing, error: fetchError } = await supabase
    .from('party_actions')
    .select('status')
    .eq('id', actionId)
    .single()

  if (fetchError) {
    throw fetchError
  }

  if (existing.status !== 'queued') {
    throw new Error(`Cannot cancel action with status '${existing.status}'. Only queued actions can be cancelled.`)
  }

  const { data, error } = await supabase
    .from('party_actions')
    .update({ status: 'cancelled' })
    .eq('id', actionId)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as PartyAction
}

/**
 * Resolve an action with results
 * Used by tick processor after processing the action
 */
export async function resolveAction(
  actionId: string,
  results: Record<string, unknown>
): Promise<PartyAction> {
  const { data, error } = await supabase
    .from('party_actions')
    .update({
      status: 'resolved',
      results,
    })
    .eq('id', actionId)
    .select()
    .single()

  if (error) {
    throw error
  }

  return data as PartyAction
}
